/**
 * The expense sheet as a value: what has been typed, what it resolves to, and
 * what is stopping it from being saved.
 *
 * `src/screens/AddExpense.tsx` is the same thing wearing React. Everything
 * that decides anything is here, as pure functions of a plain object, because
 * the interesting parts of a split editor are all arithmetic and none of them
 * need a DOM to be wrong in: which cent lands on whom, whether five modes
 * still add up to the amount, and whether re-opening a saved expense puts back
 * what somebody typed rather than a guess reverse-engineered from its cents.
 *
 * **Three rules run through the file.**
 *
 * *What was typed is what is kept.* Every mode's inputs live in their own map
 * of raw strings, keyed by user, and switching modes never throws the previous
 * mode's inputs away. Somebody who tries percentages, changes their mind, and
 * comes back finds their percentages. The strings are the state; cents are
 * derived from them and never the other way round.
 *
 * *The preview is the server's arithmetic, not a second opinion.* It goes
 * through `resolveSplit` from `src/lib/money.ts` — literally the function
 * `server/transactions.ts` calls — so the odd cent the sheet shows on somebody
 * is the odd cent the server will give them. That is only true if the
 * participants are in the group's own member order, which is what breaks the
 * tie, so `memberOrder` is a required argument everywhere it matters rather
 * than something a caller can forget.
 *
 * *The client never sends cents for a mode that says otherwise.* The preview
 * exists to be shown to a person; what is sent is the mode and its inputs, and
 * the server resolves them again. Two implementations of largest-remainder
 * rounding is how a phone and a laptop come to disagree about a penny, and
 * this app has one.
 */

import type { Allocation, ExpenseDraft, Transaction } from '../api'
import {
  centsToDigits,
  describePerson,
  describeStake,
  formatMoney,
  formatShareOf,
  parseMoneyInput,
  parsePercentInput,
  parseSignedMoneyInput,
  percentInput,
  signedMoneyInput,
  type Direction,
} from './format'
import {
  PERCENT_BIPS,
  resolveSplit,
  splitCents,
  type Split,
  type SplitMode,
  type SplitParticipant,
  type UserId,
} from './money'

/** What each mode asks a participant for, in the words the sheet uses. */
export const MODE_LABELS: Record<SplitMode, string> = {
  equal: 'Equally',
  exact: 'Exact amounts',
  percentage: 'Percentages',
  shares: 'Shares',
  adjustment: 'Adjustments',
}

export const MODE_HINTS: Record<SplitMode, string> = {
  equal: 'Everybody ticked pays the same, and the odd cent goes to whoever is shown with it.',
  exact: 'Type what each person owes. They have to add up to the amount.',
  percentage: 'Percentages of the bill, adding up to 100%.',
  shares: 'Relative shares — two adults and a child is 2, 2, 1.',
  adjustment: "Somebody's own share comes off the top; the rest is split equally.",
}

/** A person's typed input for one mode, as text. Blank counts as nothing. */
type Typed = Readonly<Record<UserId, string>>

export type SplitDraft = {
  mode: SplitMode
  /** Who is in the split. The order here is never used; `memberOrder` is. */
  included: readonly UserId[]
  /** `exact`: the cents each person owes, as money text. */
  amounts: Typed
  /** `percentage`: a percentage, as text. `12.5`, not `1250`. */
  percents: Typed
  /** `shares`: a whole number of shares, as text. */
  shares: Typed
  /** `adjustment`: signed money text — the one place a `-` is allowed. */
  adjustments: Typed
}

/** Who put money in: one person covering it, or several splitting the payment. */
export type PayerDraft = {
  /** One payer is the common case, not a special one. */
  several: boolean
  /** The sole payer, when `several` is false. */
  soleUserId: UserId
  /** Money text per payer, when `several` is true. Blank is nothing. */
  amounts: Typed
}

/** Everything the sheet holds. `src/screens/AddExpense.tsx` renders exactly this. */
export type ExpenseSheet = {
  description: string
  category: string | null
  occurredOn: string
  /** The amount pad's value. Integer cents, never a string being parsed later. */
  amountCents: number
  payers: PayerDraft
  split: SplitDraft
}

// ---------------------------------------------------------------------------
// Reading what was typed
// ---------------------------------------------------------------------------

const blank = (value: string | undefined): boolean => (value ?? '').trim() === ''

/** A whole number of shares. Blank is zero — somebody along for the ride. */
function parseShares(input: string): number | null {
  const trimmed = input.trim()
  if (trimmed === '') return 0
  if (!/^\d+$/.test(trimmed)) return null
  const shares = Number(trimmed)
  return Number.isSafeInteger(shares) ? shares : null
}

const readMoney = (input: string | undefined): number | null =>
  blank(input) ? 0 : parseMoneyInput(input!)

const readSignedMoney = (input: string | undefined): number | null =>
  blank(input) ? 0 : parseSignedMoneyInput(input!)

const readPercent = (input: string | undefined): number | null =>
  blank(input) ? 0 : parsePercentInput(input!)

// ---------------------------------------------------------------------------
// Starting a sheet, and re-opening one
// ---------------------------------------------------------------------------

const emptyTyped = (): Typed => ({})

export function newSplitDraft(memberIds: readonly UserId[]): SplitDraft {
  return {
    mode: 'equal',
    included: [...memberIds],
    amounts: emptyTyped(),
    percents: emptyTyped(),
    shares: emptyTyped(),
    adjustments: emptyTyped(),
  }
}

export function newExpenseSheet(options: {
  memberIds: readonly UserId[]
  myId: UserId
  today: string
  /**
   * Where the amount pad starts instead of zero — the receipt OCR's read,
   * when `AddExpense` opened with one. A suggestion, not a fact: it lands in
   * the same field a person would have typed into, and nothing distinguishes
   * it from a manual entry from the moment the sheet exists, because there is
   * nothing left here that would need to.
   */
  amountCents?: number
  /**
   * What the category row starts on instead of nothing — what this person
   * filed last, when the home screen's sheet opened with a pre-fill. A
   * suggestion in exactly the sense the amount above is one: it lands on the
   * control somebody would have tapped, and pressing that control is all it
   * takes to change or unset it. `src/lib/last-expense.ts` is where it comes
   * from and why.
   */
  category?: string | null
}): ExpenseSheet {
  return {
    description: '',
    category: options.category ?? null,
    occurredOn: options.today,
    amountCents: options.amountCents ?? 0,
    payers: { several: false, soleUserId: options.myId, amounts: emptyTyped() },
    split: newSplitDraft(options.memberIds),
  }
}

/**
 * What re-opening a saved split needs, and no more: the inputs it was written
 * with, and — for a row from before migration 0010, which has none — the
 * lines to read as `exact` instead.
 *
 * Narrower than `Transaction` on purpose. A recurring rule
 * (`src/lib/recurring.ts`) re-opens into the same sheet through the same two
 * functions, and a rule has no lines at all: it is inputs, resolved fresh on
 * the day each occurrence fires. So `owed` is optional, and a rule — whose
 * `split` is never `null` — never reaches the branch that would want it.
 */
export type SavedSplit = {
  splitMode: string
  split: Split | null
  owed?: readonly Allocation[]
}

/** What re-opening the payers needs. A `Transaction` and a rule are both one. */
export type SavedPayers = {
  paid: readonly Allocation[]
  amountCents: number
  createdBy: UserId
}

/**
 * A saved transaction, back in the sheet exactly as it was entered.
 *
 * The inputs come from `transaction.split`, which the server stored at write
 * time. Where that is `null` — every row written before migration 0010 — the
 * lines are read as `exact` amounts, and that is the only honest fallback
 * available: 33.33/33.33/33.34 and a third each are the same three cents, so a
 * set of cents does not say which of them was typed. Guessing would mean
 * silently rewriting somebody's expense as the price of opening it to fix a
 * typo in the description.
 *
 * An `equal` split is the one exception, because it is genuinely recoverable:
 * the mode is recorded, and "these people, equally" is exactly the people with
 * a line.
 */
export function splitDraftFrom(transaction: SavedSplit): SplitDraft {
  const draft = newSplitDraft([])

  const stored = transaction.split
  if (stored !== null && stored.participants.length > 0) {
    return {
      ...draft,
      mode: stored.mode,
      included: stored.participants.map((participant) => participant.userId),
      amounts: fromParticipants(stored, (p) =>
        p.amountCents === undefined ? undefined : centsToDigits(p.amountCents).replace(/,/g, ''),
      ),
      percents: fromParticipants(stored, (p) =>
        p.percentBips === undefined ? undefined : percentInput(p.percentBips),
      ),
      shares: fromParticipants(stored, (p) =>
        p.shares === undefined ? undefined : String(p.shares),
      ),
      adjustments: fromParticipants(stored, (p) =>
        p.adjustmentCents === undefined ? undefined : signedMoneyInput(p.adjustmentCents),
      ),
    }
  }

  const owed = transaction.owed ?? []
  const included = owed.map((allocation) => allocation.userId)
  if (transaction.splitMode === 'equal') {
    return { ...draft, mode: 'equal', included }
  }
  return {
    ...draft,
    mode: 'exact',
    included,
    amounts: Object.fromEntries(
      owed.map((allocation) => [
        allocation.userId,
        centsToDigits(allocation.amountCents).replace(/,/g, ''),
      ]),
    ),
  }
}

function fromParticipants(
  split: Split,
  textOf: (participant: SplitParticipant) => string | undefined,
): Typed {
  const typed: Record<UserId, string> = {}
  for (const participant of split.participants) {
    const value = textOf(participant)
    if (value !== undefined) typed[participant.userId] = value
  }
  return typed
}

export function payerDraftFrom(transaction: SavedPayers): PayerDraft {
  const sole =
    transaction.paid.length === 1 && transaction.paid[0].amountCents === transaction.amountCents

  return {
    several: !sole,
    soleUserId: transaction.paid[0]?.userId ?? transaction.createdBy,
    amounts: Object.fromEntries(
      transaction.paid.map((allocation) => [
        allocation.userId,
        centsToDigits(allocation.amountCents).replace(/,/g, ''),
      ]),
    ),
  }
}

export function expenseSheetFrom(transaction: Transaction): ExpenseSheet {
  return {
    description: transaction.description,
    category: transaction.category,
    occurredOn: transaction.occurredOn,
    amountCents: transaction.amountCents,
    payers: payerDraftFrom(transaction),
    split: splitDraftFrom(transaction),
  }
}

// ---------------------------------------------------------------------------
// Switching modes
// ---------------------------------------------------------------------------

/**
 * Moves the split to another mode, seeding that mode's blank inputs so the
 * sheet lands on something that already reconciles.
 *
 * The seed is always the equal split, in the new mode's own units — one share
 * each, a hundredth-exact equal percentage, no adjustment, or the cents the
 * split currently resolves to. Switching mode is a change of *how* the bill is
 * being described, and describing the same bill a different way should not
 * start by breaking it.
 *
 * Only blanks are filled. Inputs somebody has already typed survive being
 * switched away from and back, which is the whole reason each mode keeps its
 * own map.
 */
export function withMode(
  split: SplitDraft,
  mode: SplitMode,
  amountCents: number,
  memberOrder: readonly UserId[],
): SplitDraft {
  const people = includedInOrder(split, memberOrder)
  if (people.length === 0) return { ...split, mode }

  const seeded = { ...split, mode }

  switch (mode) {
    case 'exact': {
      // The cents it resolves to right now, so the amounts start out already
      // adding up to the amount. If the current mode does not resolve there is
      // nothing honest to seed from, and the sheet says what is missing.
      const preview = previewSplit(amountCents, split, memberOrder)
      if (preview.error !== null) return seeded
      return {
        ...seeded,
        amounts: fill(split.amounts, preview.allocations, (allocation) =>
          centsToDigits(allocation.amountCents).replace(/,/g, ''),
        ),
      }
    }

    case 'percentage': {
      // Largest-remainder on the basis points themselves, so three people are
      // 33.34 / 33.33 / 33.33 — which is exactly 100%, and puts the extra
      // hundredth on the same person the odd cent would go to.
      const bips = splitCents(PERCENT_BIPS, people.map(() => 1))
      return {
        ...seeded,
        percents: fill(
          split.percents,
          people.map((userId, index) => ({ userId, amountCents: bips[index] })),
          (entry) => percentInput(entry.amountCents),
        ),
      }
    }

    case 'shares':
      return {
        ...seeded,
        shares: fill(
          split.shares,
          people.map((userId) => ({ userId, amountCents: 1 })),
          () => '1',
        ),
      }

    case 'adjustment':
      return {
        ...seeded,
        adjustments: fill(
          split.adjustments,
          people.map((userId) => ({ userId, amountCents: 0 })),
          () => '0.00',
        ),
      }

    case 'equal':
      return seeded
  }
}

function fill(
  typed: Typed,
  entries: readonly Allocation[],
  textOf: (entry: Allocation) => string,
): Typed {
  const next: Record<UserId, string> = { ...typed }
  for (const entry of entries) {
    if (blank(next[entry.userId])) next[entry.userId] = textOf(entry)
  }
  return next
}

/** Ticks or unticks somebody. Their typed inputs are kept either way. */
export function toggleParticipant(split: SplitDraft, userId: UserId): SplitDraft {
  const included = split.included.includes(userId)
    ? split.included.filter((id) => id !== userId)
    : [...split.included, userId]
  return { ...split, included }
}

export function withTyped(
  split: SplitDraft,
  field: 'amounts' | 'percents' | 'shares' | 'adjustments',
  userId: UserId,
  value: string,
): SplitDraft {
  return { ...split, [field]: { ...split[field], [userId]: value } }
}

// ---------------------------------------------------------------------------
// Resolving
// ---------------------------------------------------------------------------

/**
 * The included participants, in the group's member order.
 *
 * The order is the group's and not the sheet's, because `splitCents` breaks a
 * tie on an odd cent by position: passing people in the order their checkboxes
 * happened to be ticked would predict the cent onto the wrong person, and then
 * the server's answer would move it.
 */
export function includedInOrder(
  split: SplitDraft,
  memberOrder: readonly UserId[],
): UserId[] {
  const included = new Set(split.included)
  return memberOrder.filter((userId) => included.has(userId))
}

/** A refusal a person can act on: which field, and what is wrong with it. */
export type Problem = string | null

/**
 * The split as `resolveSplit` will read it, or a sentence saying what is not
 * readable yet.
 */
export function participantsFor(
  split: SplitDraft,
  memberOrder: readonly UserId[],
): { participants: SplitParticipant[]; error: Problem } {
  const people = includedInOrder(split, memberOrder)
  if (people.length === 0) {
    return { participants: [], error: 'Nobody is in this split yet.' }
  }

  const participants: SplitParticipant[] = []
  for (const userId of people) {
    const participant: SplitParticipant = { userId }
    switch (split.mode) {
      case 'equal':
        break
      case 'exact': {
        const cents = readMoney(split.amounts[userId])
        if (cents === null) return bad(`${split.amounts[userId]} is not an amount.`)
        participant.amountCents = cents
        break
      }
      case 'percentage': {
        const bips = readPercent(split.percents[userId])
        if (bips === null) {
          return bad(`${split.percents[userId]} is not a percentage, to the hundredth.`)
        }
        participant.percentBips = bips
        break
      }
      case 'shares': {
        const shares = parseShares(split.shares[userId] ?? '')
        if (shares === null) return bad(`${split.shares[userId]} is not a whole number of shares.`)
        participant.shares = shares
        break
      }
      case 'adjustment': {
        const cents = readSignedMoney(split.adjustments[userId])
        if (cents === null) return bad(`${split.adjustments[userId]} is not an amount.`)
        participant.adjustmentCents = cents
        break
      }
    }
    participants.push(participant)
  }

  return { participants, error: null }
}

const bad = (message: string) => ({ participants: [] as SplitParticipant[], error: message })

export type SplitPreview = {
  /**
   * One row per participant, in member order, including anybody who comes out
   * at zero — a screen wants to say "nothing" rather than not mention them.
   */
  allocations: Allocation[]
  /** What the rows add up to. Equal to the amount whenever `error` is null. */
  totalCents: number
  error: Problem
}

/**
 * What each person will owe — resolved through `resolveSplit`, so this is a
 * preview of the server's answer rather than an approximation of it.
 *
 * The odd cent is *in* these numbers rather than hidden behind them: $81.31
 * four ways comes back as 20.33 / 20.33 / 20.33 / 20.32, and the person
 * carrying the 20.32 is the same person every time it is recalculated, because
 * the participants are in the group's member order and `splitCents` breaks its
 * tie on position.
 */
export function previewSplit(
  amountCents: number,
  split: SplitDraft,
  memberOrder: readonly UserId[],
): SplitPreview {
  const { participants, error } = participantsFor(split, memberOrder)
  if (error !== null) return { allocations: [], totalCents: 0, error }
  if (amountCents <= 0) {
    return { allocations: [], totalCents: 0, error: 'Put in an amount first.' }
  }

  try {
    const allocations = resolveSplit(amountCents, { mode: split.mode, participants })
    return {
      allocations,
      totalCents: allocations.reduce((sum, entry) => sum + entry.amountCents, 0),
      error: null,
    }
  } catch (err) {
    // Every throw in money.ts is a RangeError naming the sum that did not come
    // out. It is the same sentence the server would refuse with, which is the
    // point of previewing through the same module.
    return { allocations: [], totalCents: 0, error: (err as Error).message }
  }
}

/** Who paid what, in member order, or a sentence saying why it does not work. */
export function payersFor(
  payers: PayerDraft,
  amountCents: number,
  memberOrder: readonly UserId[],
): { payers: Allocation[]; error: Problem } {
  if (!payers.several) {
    if (!memberOrder.includes(payers.soleUserId)) {
      return { payers: [], error: 'Say who paid.' }
    }
    return { payers: [{ userId: payers.soleUserId, amountCents }], error: null }
  }

  const entries: Allocation[] = []
  let given = 0
  for (const userId of memberOrder) {
    const cents = readMoney(payers.amounts[userId])
    if (cents === null) return { payers: [], error: `${payers.amounts[userId]} is not an amount.` }
    given += cents
    if (cents > 0) entries.push({ userId, amountCents: cents })
  }

  if (entries.length === 0) return { payers: [], error: 'Nobody has put anything in yet.' }
  if (given !== amountCents) {
    return {
      payers: [],
      error: `The payers put in ${centsToDigits(given)}, which is not the ${centsToDigits(
        amountCents,
      )} being split.`,
    }
  }
  return { payers: entries, error: null }
}

// ---------------------------------------------------------------------------
// The default path
// ---------------------------------------------------------------------------

/**
 * Whether who-paid and how-split are still exactly what a new sheet opens on:
 * one person — you — covered it, and it is split equally between everybody in
 * the group.
 *
 * This is the question the sheet asks itself to decide whether to *show* the
 * payer and split editors at all. Nearly every expense anybody files is this
 * shape, and it already takes no taps to agree with; what it used to cost was
 * two fieldsets and a scroll between the amount and the button. So the sheet
 * states the default in a sentence (`describeShare` below) and keeps the
 * editors one tap away, and correcting an expense that is *not* this shape
 * opens them, because hiding the thing somebody came to change would be worse
 * than the scroll.
 *
 * "Everybody" is the ids the sheet was opened with rather than the group as it
 * stands: a sheet correcting an old expense may carry somebody who has since
 * left, and they are as much a default part of *that* expense as anybody else.
 */
export function isDefaultShare(
  sheet: ExpenseSheet,
  myId: UserId,
  memberIds: readonly UserId[],
): boolean {
  if (sheet.payers.several) return false
  if (sheet.payers.soleUserId !== myId) return false
  if (sheet.split.mode !== 'equal') return false

  const included = new Set(sheet.split.included)
  return included.size === memberIds.length && memberIds.every((userId) => included.has(userId))
}

/**
 * Who paid and how it is split, as one sentence — the line that stands in for
 * the two editors while they are folded away.
 *
 * It resolves through `previewSplit`, so the per-person figure in it is the
 * same cents `SplitEditor` would have drawn in its right-hand column and the
 * same cents the server will write. The figure is only stated when everybody
 * owes the same, because "$20.33 each" about a split that is really
 * 20.33 / 20.33 / 20.33 / 20.32 is precisely the rounding-away this app does
 * not do: an uneven split says how many people are in it and leaves the cents
 * to the editor that shows all of them.
 *
 * When it is one payer and one other person, it says who owes whom — "You
 * paid, and Hana owes you 100%", "split equally between 2 — Hana owes you
 * $50.00 (50%)" — because that is the whole of what the expense does, and the
 * question somebody filing it is actually asking.
 */
export function describeShare(options: {
  sheet: ExpenseSheet
  myId: UserId
  /** In the group's own order — the order an odd cent is broken by. */
  memberOrder: readonly UserId[]
  /** A member's name, as this screen writes it. */
  nameOf: (userId: UserId) => string
  currency: string
}): string {
  const { sheet, myId, memberOrder, nameOf, currency } = options

  const who = describePayers(sheet, memberOrder, myId, nameOf)
  const people = includedInOrder(sheet.split, memberOrder)
  const sole = sheet.payers.several ? null : sheet.payers.soleUserId
  const owes = (debtor: UserId) => describeOwing(debtor, sole, myId, nameOf)

  if (people.length === 0) return `${who}, and nobody is in the split yet.`
  if (people.length === 1) {
    const only = people[0]
    if (only === sole) {
      return only === myId
        ? 'You paid, just for yourself — nobody owes anything.'
        : `${nameOf(only)} paid, just for themselves — nobody owes anything.`
    }
    return `${who}, and ${owes(only)} 100%.`
  }

  const how =
    sheet.split.mode === 'equal'
      ? `split equally between ${people.length}`
      : `split by ${MODE_LABELS[sheet.split.mode].toLowerCase()} between ${people.length}`

  // One payer, and one other person: say what that other person owes.
  if (sole !== null && people.length === 2 && people.includes(sole)) {
    const other = people[0] === sole ? people[1] : people[0]
    const preview = previewSplit(sheet.amountCents, sheet.split, memberOrder)
    const share = preview.allocations.find((entry) => entry.userId === other)?.amountCents
    if (preview.error === null && share !== undefined) {
      return `${who}, ${how} — ${owes(other)} ${formatMoney(share, currency)} (${formatShareOf(
        share,
        sheet.amountCents,
      )}).`
    }
  }

  return `${who}, ${how}${describeEach(sheet, memberOrder, currency)}.`
}

/**
 * "Hana owes you", "you owe Ben", "Hana owes Ben" — or, when several people
 * paid and there is no single person it is owed to, "Hana owes".
 */
function describeOwing(
  debtor: UserId,
  creditor: UserId | null,
  myId: UserId,
  nameOf: (userId: UserId) => string,
): string {
  if (creditor === null) return debtor === myId ? 'you owe' : `${nameOf(debtor)} owes`
  if (debtor === myId) return `you owe ${nameOf(creditor)}`
  if (creditor === myId) return `${nameOf(debtor)} owes you`
  return `${nameOf(debtor)} owes ${nameOf(creditor)}`
}

// ---------------------------------------------------------------------------
// What this expense does to everybody
// ---------------------------------------------------------------------------

/** One person's part in an expense still being edited. */
export type SettlementRow = {
  userId: UserId
  paidCents: number
  /** Their share of the bill. */
  owesCents: number
  /** What they paid less their share: positive is money coming back to them. */
  direction: Direction
  netCents: number
  /** `describeStake`'s words: "you lent", "borrowed", "square". */
  label: string
}

export type SettlementPreview = {
  /** Everybody who paid or owes, in member order. Empty while `error` is set. */
  rows: SettlementRow[]
  /**
   * "Hana owes you $50.00" — one per person, but only when a single person
   * paid. With several payers there is no one person any debt is owed *to*;
   * the group's balances net it out, and the rows above say each person's net.
   */
  owing: string[]
  error: Problem
}

/**
 * What filing the sheet as it stands would do to each person's balance: what
 * they put in, what their share is, and the difference.
 *
 * It is `payersFor` and `previewSplit` — the same two readings `resolveSheet`
 * sends — laid side by side, so the preview cannot say anything the saved
 * expense will not. The words come from `describeStake`, the function the
 * group's list uses for each row, and `describePerson`, the one the balances
 * use, so what the sheet previews is what those screens will say.
 */
export function previewSettlement(options: {
  sheet: ExpenseSheet
  myId: UserId
  memberOrder: readonly UserId[]
  nameOf: (userId: UserId) => string
  currency: string
}): SettlementPreview {
  const { sheet, myId, memberOrder, nameOf, currency } = options

  const split = previewSplit(sheet.amountCents, sheet.split, memberOrder)
  if (split.error !== null) return { rows: [], owing: [], error: split.error }
  const payers = payersFor(sheet.payers, sheet.amountCents, memberOrder)
  if (payers.error !== null) return { rows: [], owing: [], error: payers.error }

  const paidOf = new Map(payers.payers.map((entry) => [entry.userId, entry.amountCents]))
  const owedOf = new Map(split.allocations.map((entry) => [entry.userId, entry.amountCents]))

  const rows: SettlementRow[] = memberOrder
    .filter((userId) => paidOf.has(userId) || owedOf.has(userId))
    .map((userId) => {
      const stake = describeStake(userId, payers.payers, split.allocations, currency, userId === myId)
      return {
        userId,
        paidCents: paidOf.get(userId) ?? 0,
        owesCents: owedOf.get(userId) ?? 0,
        direction: stake.direction,
        netCents: stake.cents,
        label: stake.label,
      }
    })

  const owing: string[] = []
  const creditor = payers.payers.length === 1 ? payers.payers[0].userId : null
  if (creditor !== null) {
    for (const entry of split.allocations) {
      if (entry.userId === creditor || entry.amountCents === 0) continue
      if (creditor === myId) {
        owing.push(describePerson(nameOf(entry.userId), entry.amountCents, currency).sentence)
      } else if (entry.userId === myId) {
        owing.push(describePerson(nameOf(creditor), -entry.amountCents, currency).sentence)
      } else {
        owing.push(
          `${nameOf(entry.userId)} owes ${nameOf(creditor)} ${formatMoney(entry.amountCents, currency)}`,
        )
      }
    }
  }

  return { rows, owing, error: null }
}

/** "You paid", "Ben paid", "3 people paid" — the first half of the sentence. */
function describePayers(
  sheet: ExpenseSheet,
  memberOrder: readonly UserId[],
  myId: UserId,
  nameOf: (userId: UserId) => string,
): string {
  if (!sheet.payers.several) {
    return sheet.payers.soleUserId === myId ? 'You paid' : `${nameOf(sheet.payers.soleUserId)} paid`
  }

  // Whoever has a figure against them. `payersFor` refuses the whole reading
  // while the amounts do not add up to the bill, and this sentence is drawn
  // long before they do, so a half-typed list falls back to counting the boxes
  // that are not blank rather than to saying nothing.
  const reading = payersFor(sheet.payers, sheet.amountCents, memberOrder)
  const named =
    reading.error === null
      ? reading.payers.map((allocation) => allocation.userId)
      : memberOrder.filter((userId) => !blank(sheet.payers.amounts[userId]))

  if (named.length === 1) return `${nameOf(named[0])} paid`
  if (named.length > 1) return `${named.length} people paid`
  return 'Several people paid'
}

/** " — $6.25 each", but only when it really is the same each. */
function describeEach(
  sheet: ExpenseSheet,
  memberOrder: readonly UserId[],
  currency: string,
): string {
  const preview = previewSplit(sheet.amountCents, sheet.split, memberOrder)
  if (preview.error !== null) return ''

  const first = preview.allocations[0]?.amountCents
  if (first === undefined) return ''
  if (!preview.allocations.every((allocation) => allocation.amountCents === first)) return ''
  return ` — ${formatMoney(first, currency)} each`
}

// ---------------------------------------------------------------------------
// The whole sheet
// ---------------------------------------------------------------------------

export type ResolvedSheet = {
  /** Ready to send, or `null` while anything below is still a sentence. */
  draft: ExpenseDraft | null
  preview: SplitPreview
  problems: {
    description: Problem
    amount: Problem
    payers: Problem
    split: Problem
  }
}

/**
 * The sheet, resolved: the preview to draw, what is still wrong, and — when
 * nothing is — the draft to send.
 *
 * The draft carries the mode and its inputs, never the previewed cents. The
 * server resolves it again through the same module, so the preview above the
 * Save button and the answer that comes back are the same arithmetic run
 * twice, not two implementations of it agreeing by luck.
 */
export function resolveSheet(
  sheet: ExpenseSheet,
  memberOrder: readonly UserId[],
): ResolvedSheet {
  const preview = previewSplit(sheet.amountCents, sheet.split, memberOrder)
  const payers = payersFor(sheet.payers, sheet.amountCents, memberOrder)

  const problems = {
    description: sheet.description.trim() === '' ? 'Say what it was.' : null,
    amount: sheet.amountCents <= 0 ? 'Put in an amount.' : null,
    payers: sheet.amountCents <= 0 ? null : payers.error,
    split: preview.error,
  }

  const ready =
    problems.description === null &&
    problems.amount === null &&
    problems.payers === null &&
    problems.split === null

  if (!ready) return { draft: null, preview, problems }

  const { participants } = participantsFor(sheet.split, memberOrder)
  const split: Split = { mode: sheet.split.mode, participants }

  return {
    draft: {
      description: sheet.description.trim(),
      category: sheet.category,
      amountCents: sheet.amountCents,
      occurredOn: sheet.occurredOn,
      payers: payers.payers,
      split,
    },
    preview,
    problems,
  }
}
